'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { translations, Language } from '@/locales';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  tRaw: (key: string) => any;
  formatNumber: (value: number, locale?: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>('de');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && ['en', 'de'].includes(savedLanguage)) {
      setLanguageState(savedLanguage);
    } else {
      // Set German as default if no saved language or invalid language
      setLanguageState('de');
      localStorage.setItem('language', 'de');
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string, vars?: Record<string, string | number>): string => {
    // Split the key by dots to navigate through nested objects
    const keys = key.split('.');
    let result: any = translations[language];
    
    for (const k of keys) {
      if (result && typeof result === 'object' && k in result) {
        result = result[k];
      } else {
        // Return the key itself if translation not found
        return key;
      }
    }
    
    // Handle variable replacement in translation strings
    if (typeof result === 'string' && vars) {
      return result.replace(/\{(\w+)\}/g, (match, varName) => {
        return vars[varName] !== undefined ? String(vars[varName]) : match;
      });
    }
    
    return typeof result === 'string' ? result : key;
  };

  const tRaw = (key: string): any => {
    // Split the key by dots to navigate through nested objects
    const keys = key.split('.');
    let result: any = translations[language];
    
    for (const k of keys) {
      if (result && typeof result === 'object' && k in result) {
        result = result[k];
      } else {
        // Return undefined if translation not found
        return undefined;
      }
    }
    
    return result;
  };

  const formatNumber = (value: number, locale?: string): string => {
    const localeString = locale || (language === 'de' ? 'de-AT' : 'en-US');
    return new Intl.NumberFormat(localeString, {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
    }).format(value);
  };

  return (
    <LanguageContext.Provider data-component-id="src/contexts/LanguageContext.tsx:86:4" data-component-path="src/contexts/LanguageContext.tsx" data-component-line="86" data-component-end-line="88" data-component-file="LanguageContext.tsx" data-component-name="LanguageContext.Provider" data-component-content="%7B%22elementName%22%3A%22LanguageContext.Provider%22%7D" value={{ language, setLanguage, t, tRaw, formatNumber }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Export Language type for use in other files
export type { Language };